<script setup>
import { ref, reactive, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import MetaBusinessAgentAPI from 'dashboard/api/metaBusinessAgent';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const props = defineProps({
  hookId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const T = key =>
  t(`INTEGRATION_SETTINGS.META_BUSINESS_AGENT.BUSINESS_INFO.${key}`);

const isSaving = ref(false);
const form = reactive({
  payment_method: '',
  return_policy: '',
  purchase_info: '',
  delivery_and_shipping: '',
  business_description: '',
  contact_info: { email: '', hours_of_operation: '', address: '' },
});

const fetchBusinessInfo = async () => {
  try {
    const { data } = await MetaBusinessAgentAPI.getBusinessInfo(props.hookId);
    Object.assign(form, data);
    form.contact_info = {
      email: '',
      hours_of_operation: '',
      address: '',
      ...data.contact_info,
    };
  } catch (error) {
    useAlert(T('API.FETCH_ERROR'));
  }
};

onMounted(fetchBusinessInfo);

const save = async () => {
  isSaving.value = true;
  try {
    await MetaBusinessAgentAPI.updateBusinessInfo(props.hookId, form);
    useAlert(T('API.UPDATE_SUCCESS'));
  } catch (error) {
    useAlert(T('API.UPDATE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};
</script>

<template>
  <div class="flex flex-col gap-4 max-w-2xl">
    <p class="text-sm text-n-slate-11">{{ T('DESCRIPTION') }}</p>
    <TextArea
      v-model="form.business_description"
      :label="T('DESCRIPTION_LABEL')"
      :max-length="2000"
    />
    <TextArea
      v-model="form.payment_method"
      :label="T('PAYMENT_METHOD_LABEL')"
      :max-length="2000"
    />
    <TextArea
      v-model="form.return_policy"
      :label="T('RETURN_POLICY_LABEL')"
      :max-length="2000"
    />
    <TextArea
      v-model="form.purchase_info"
      :label="T('PURCHASE_INFO_LABEL')"
      :max-length="2000"
    />
    <TextArea
      v-model="form.delivery_and_shipping"
      :label="T('DELIVERY_LABEL')"
      :max-length="2000"
    />
    <Input
      v-model="form.contact_info.email"
      :label="T('EMAIL_LABEL')"
      type="email"
    />
    <Input
      v-model="form.contact_info.hours_of_operation"
      :label="T('HOURS_LABEL')"
    />
    <Input v-model="form.contact_info.address" :label="T('ADDRESS_LABEL')" />
    <div class="flex justify-end">
      <Button :label="T('SAVE_BUTTON')" :is-loading="isSaving" @click="save" />
    </div>
  </div>
</template>
